<template>
   <div>
      <navbar-component />
      <main class="bg-blueGray-100">
         <!-- Hero: ICP problem → WaveData Cloud answer -->
         <section
            class="relative overflow-hidden pt-16 py-20 md:py-28"
            :style="{ backgroundColor: '#111827' }"
         >
            <div
               class="absolute inset-0 bg-center bg-cover"
               :style="{ backgroundImage: `url(${heroImage})`, opacity: 0.45 }"
            ></div>
            <div
               class="absolute inset-0"
               :style="{ backgroundColor: 'rgba(0, 0, 0, 0.55)' }"
            ></div>
            <div class="container relative mx-auto px-4 md:px-6">
               <div class="max-w-3xl">
                  <span
                     class="inline-block bg-blue-600 text-white text-xs font-bold tracking-wider uppercase px-3 py-1 rounded-full mb-5 shadow-sm"
                  >
                     Frac operations in the cloud
                  </span>
                  <h1
                     class="text-white font-bold text-4xl md:text-5xl lg:text-6xl leading-tight mb-5"
                  >
                     WaveData Cloud
                  </h1>
                  <p
                     class="text-blueGray-100 text-lg md:text-xl leading-relaxed max-w-2xl mb-4"
                  >
                     Area supervisors, yard leads, remote experts, and granted
                     clients still need the same live picture the van sees—without
                     riding to every pad.
                  </p>
                  <p
                     class="text-blueGray-300 text-base md:text-lg leading-relaxed max-w-2xl mb-8"
                  >
                     WaveData Cloud is that picture: company-wide fleet monitoring,
                     configurable cloud alarms, and full historical analysis—remote
                     viewing and optional control of the same job WaveCommand7 runs
                     in the data van.
                  </p>
                  <div class="flex flex-wrap gap-3">
                     <router-link
                        :to="{ path: '/', hash: '#contact' }"
                        class="inline-block bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
                     >
                        Book a Demo with AMI
                        <i class="fas fa-arrow-right ml-2"></i>
                     </router-link>
                  </div>
               </div>
            </div>
         </section>

         <!-- ICP problem cards -->
         <section class="py-16 md:py-20 bg-blueGray-800">
            <div class="container mx-auto px-4 md:px-6">
               <div class="text-center max-w-4xl mx-auto mb-10 md:mb-12">
                  <h2 class="text-3xl md:text-4xl font-bold text-white mb-3">
                     Problems ops leadership actually faces
                  </h2>
                  <p class="text-lg text-blueGray-300 max-w-3xl mx-auto">
                     WaveData Cloud is aimed at the friction that shows up when
                     the job is live on the pad—but the people who need answers
                     are somewhere else.
                  </p>
               </div>
               <div class="grid md:grid-cols-2 gap-5 md:gap-6 max-w-5xl mx-auto">
                  <div
                     v-for="item in painPoints"
                     :key="item.title"
                     class="bg-blueGray-900 rounded-xl p-6 border border-blueGray-700"
                  >
                     <div class="flex items-center gap-3 mb-3">
                        <div
                           class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border-2"
                           :class="item.iconWrapClass"
                        >
                           <i :class="item.icon"></i>
                        </div>
                        <h3 class="text-xl font-bold text-white leading-tight">
                           {{ item.title }}
                        </h3>
                     </div>
                     <p class="text-blueGray-400 text-sm mb-3">
                        <span class="font-semibold text-blueGray-300"
                           >The challenge:</span
                        >
                        {{ item.challenge }}
                     </p>
                     <p class="text-blueGray-200 text-sm md:text-base">
                        <span class="font-semibold text-lightBlue-300"
                           >How WaveData Cloud helps:</span
                        >
                        {{ item.solution }}
                     </p>
                  </div>
               </div>
               <div class="text-center max-w-3xl mx-auto mt-12 mb-6">
                  <h3 class="text-2xl md:text-3xl font-bold text-white mb-3">
                     The same job—from anywhere you grant access
                  </h3>
                  <p class="text-blueGray-300 text-base md:text-lg leading-relaxed">
                     WaveCommand7 runs the stage in the data van. WaveData Cloud
                     is the remote layer: live fleets, custom screens, alarms, and
                     history in the browser—so supervisors, remote experts, and
                     granted clients see the job without being on location.
                  </p>
               </div>
               <div
                  class="rounded-xl overflow-hidden border border-blueGray-700 shadow-lg bg-blueGray-900 max-w-5xl mx-auto"
               >
                  <img
                     :src="fleetviewImage"
                     alt="WaveData Cloud fleets view showing company-wide spreads"
                     class="w-full h-auto block"
                  />
                  <p
                     class="p-3 text-center text-sm text-blueGray-400 tracking-wide"
                  >
                     Fleets—every spread’s connection, treating status, and iron at a glance
                  </p>
               </div>
            </div>
         </section>

         <!-- Cloud monitoring -->
         <section class="py-16 md:py-20 bg-blueGray-900">
            <div class="container mx-auto px-4 md:px-6">
               <div class="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-6xl mx-auto">
                  <div>
                     <p
                        class="text-xs font-bold uppercase tracking-wide text-lightBlue-300 mb-2"
                     >
                        Cloud monitoring
                     </p>
                     <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
                        See every fleet without being on location
                     </h2>
                     <p class="text-blueGray-300 text-lg mb-5 leading-relaxed">
                        When you manage more than one pad, “call the van” is not a
                        monitoring strategy. WaveData Cloud gives area managers,
                        maintenance supervisors, and remote experts a live view of
                        which fleets are online, which are treating, and how the
                        iron is running.
                     </p>
                     <ul class="space-y-2">
                        <li
                           v-for="bullet in monitoringBullets"
                           :key="bullet"
                           class="flex items-start gap-3 text-blueGray-200"
                        >
                           <i
                              class="fas fa-check-circle text-emerald-400 mt-1 flex-shrink-0"
                           ></i>
                           <span>{{ bullet }}</span>
                        </li>
                     </ul>
                  </div>
                  <div class="flex flex-col gap-4">
                     <div
                        v-for="shot in monitoringShots"
                        :key="shot.title"
                        class="rounded-xl overflow-hidden border border-blueGray-700 shadow-lg bg-blueGray-800"
                     >
                        <img
                           :src="shot.image"
                           :alt="shot.alt"
                           class="w-full h-auto block"
                        />
                        <p
                           class="p-3 text-center text-sm text-blueGray-400 tracking-wide"
                        >
                           {{ shot.title }}
                        </p>
                     </div>
                  </div>
               </div>
            </div>
         </section>

         <!-- Cloud alarming -->
         <section class="py-16 md:py-20 bg-blueGray-800">
            <div class="container mx-auto px-4 md:px-6">
               <div class="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-6xl mx-auto">
                  <div
                     class="rounded-xl overflow-hidden border border-blueGray-700 shadow-lg bg-blueGray-900 lg:order-1"
                  >
                     <img
                        :src="alarmRulesImage"
                        alt="WaveData Cloud alarm rules with email and SMS actions"
                        class="w-full h-auto block"
                     />
                     <p
                        class="p-3 text-center text-sm text-blueGray-400 tracking-wide"
                     >
                        Alarm rules—severity, timing, and email / SMS actions
                     </p>
                  </div>
                  <div class="lg:order-2">
                     <p
                        class="text-xs font-bold uppercase tracking-wide text-orange-300 mb-2"
                     >
                        Cloud alarming
                     </p>
                     <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
                        Know when something needs attention—before the next call
                     </h2>
                     <p class="text-blueGray-300 text-lg mb-5 leading-relaxed">
                        Issues that only surface on a van screen stay invisible to
                        everyone else until someone dials out. Cloud alarming puts
                        live alarms, event history, and rule-based notifications in
                        front of the people who can escalate or assist.
                     </p>
                     <ul class="space-y-2">
                        <li
                           v-for="bullet in alarmBullets"
                           :key="bullet"
                           class="flex items-start gap-3 text-blueGray-200"
                        >
                           <i
                              class="fas fa-check-circle text-emerald-400 mt-1 flex-shrink-0"
                           ></i>
                           <span>{{ bullet }}</span>
                        </li>
                     </ul>
                  </div>
               </div>
               <div class="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto mt-10">
                  <div
                     v-for="item in alarmPoints"
                     :key="item.title"
                     class="bg-blueGray-900 rounded-xl p-5 border border-blueGray-700"
                  >
                     <h3 class="text-lg font-bold text-white mb-2">
                        {{ item.title }}
                     </h3>
                     <p class="text-blueGray-300 text-sm">{{ item.text }}</p>
                  </div>
               </div>
            </div>
         </section>

         <!-- Historical analysis -->
         <section class="py-16 md:py-20 bg-blueGray-900">
            <div class="container mx-auto px-4 md:px-6">
               <div class="text-center max-w-4xl mx-auto mb-10 md:mb-12">
                  <p
                     class="text-xs font-bold uppercase tracking-wide text-emerald-300 mb-2"
                  >
                     Historical, data-driven analysis
                  </p>
                  <h2 class="text-3xl md:text-4xl font-bold text-white mb-3">
                     Look back after the fact—with the full stage picture
                  </h2>
                  <p class="text-lg text-blueGray-300 max-w-3xl mx-auto">
                     When a stage ends, tribal memory is not enough. WaveData keeps
                     the history so you can investigate issues, review how equipment
                     was operated, and check performance with charts and CSV export.
                  </p>
               </div>
               <div class="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto mb-10">
                  <div
                     v-for="shot in historyShots"
                     :key="shot.title"
                     class="rounded-xl overflow-hidden border border-blueGray-700 shadow-lg bg-blueGray-800"
                  >
                     <img
                        :src="shot.image"
                        :alt="shot.alt"
                        class="w-full h-auto block"
                     />
                     <div class="p-4">
                        <h3 class="text-lg font-bold text-white mb-1">
                           {{ shot.title }}
                        </h3>
                        <p class="text-blueGray-300 text-sm">{{ shot.text }}</p>
                     </div>
                  </div>
               </div>
               <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
                  <div
                     v-for="item in historyPoints"
                     :key="item.title"
                     class="bg-blueGray-800 rounded-xl p-5 border border-blueGray-700"
                  >
                     <h3 class="text-lg font-bold text-white mb-2">
                        {{ item.title }}
                     </h3>
                     <p class="text-blueGray-300 text-sm">{{ item.text }}</p>
                  </div>
               </div>
            </div>
         </section>

         <!-- Custom screens + who it's for -->
         <section class="py-16 md:py-20 bg-blueGray-800">
            <div class="container mx-auto px-4 md:px-6">
               <div class="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-6xl mx-auto mb-12">
                  <div>
                     <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
                        Custom screens built for the channels you care about
                     </h2>
                     <p class="text-blueGray-300 text-lg mb-5 leading-relaxed">
                        AMI ships a large set of screen setups. Engineers and
                        technicians with permission can also build their own—
                        meters, charts, gauges, active alarms, and more—so remote
                        viewers see the same operational picture as the van.
                     </p>
                     <ul class="space-y-2">
                        <li
                           v-for="bullet in screenBullets"
                           :key="bullet"
                           class="flex items-start gap-3 text-blueGray-200"
                        >
                           <i
                              class="fas fa-check-circle text-emerald-400 mt-1 flex-shrink-0"
                           ></i>
                           <span>{{ bullet }}</span>
                        </li>
                     </ul>
                  </div>
                  <div
                     class="rounded-xl overflow-hidden border border-blueGray-700 shadow-lg bg-blueGray-900"
                  >
                     <img
                        :src="customScreensImage"
                        alt="WaveData Cloud customizable dashboards"
                        class="w-full h-auto block"
                     />
                     <p
                        class="p-3 text-center text-sm text-blueGray-400 tracking-wide"
                     >
                        Custom screens—live layouts for the job you are watching
                     </p>
                  </div>
               </div>

               <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
                  <div
                     v-for="role in audience"
                     :key="role.title"
                     class="bg-blueGray-900 rounded-xl p-5 border border-blueGray-700"
                  >
                     <p
                        class="text-xs font-bold uppercase tracking-wide mb-1"
                        :class="role.labelClass"
                     >
                        {{ role.label }}
                     </p>
                     <h3 class="text-lg font-bold text-white mb-2">
                        {{ role.title }}
                     </h3>
                     <p class="text-blueGray-300 text-sm">{{ role.text }}</p>
                  </div>
               </div>
            </div>
         </section>

         <!-- Pricing tiers -->
         <section class="py-16 md:py-20 bg-blueGray-900">
            <div class="container mx-auto px-4 md:px-6">
               <div class="text-center max-w-4xl mx-auto mb-10 md:mb-12">
                  <h2 class="text-3xl md:text-4xl font-bold text-white mb-3">
                     Choose the tier that fits how you work
                  </h2>
                  <p class="text-lg text-blueGray-300 max-w-3xl mx-auto">
                     From file collection to live monitoring and alarms—scale
                     WaveData as your visibility and analysis needs grow.
                  </p>
               </div>

               <div class="grid lg:grid-cols-3 gap-5 md:gap-6 max-w-6xl mx-auto">
                  <div
                     v-for="tier in tiers"
                     :key="tier.name"
                     class="bg-blueGray-800 rounded-xl p-6 md:p-7 border border-blueGray-700 flex flex-col"
                  >
                     <div class="text-center mb-2">
                        <h3
                           class="text-3xl font-bold mb-2 flex items-center justify-center gap-3"
                        >
                           <span class="text-blueGray-400 text-lg font-semibold"
                              >Tier</span
                           >
                           <span :style="{ color: tier.accent }">{{
                              tier.name
                           }}</span>
                        </h3>
                        <p class="text-blueGray-400 text-sm mb-6">
                           {{ tier.tagline }}
                        </p>
                     </div>
                     <div class="h-px w-full bg-blueGray-700 mb-6"></div>
                     <p class="text-blueGray-300 text-sm mb-5">
                        {{ tier.summary }}
                     </p>
                     <ul class="space-y-3 flex-1">
                        <li
                           v-for="feature in visibleTierFeatures(tier)"
                           :key="feature.title"
                           class="flex items-start gap-3"
                        >
                           <i
                              class="fas fa-check text-emerald-400 mt-1 flex-shrink-0"
                           ></i>
                           <div>
                              <span
                                 class="font-semibold text-sm"
                                 :style="{ color: tier.accent }"
                                 >{{ feature.title }}</span
                              >
                              <p class="text-blueGray-400 text-sm mt-0.5">
                                 {{ feature.text }}
                              </p>
                           </div>
                        </li>
                     </ul>
                     <button
                        v-if="tier.features.length > tier.previewCount"
                        type="button"
                        class="mt-5 text-sm font-semibold self-start hover:underline focus:outline-none"
                        :style="{ color: tier.accent }"
                        @click="toggleTier(tier.name)"
                     >
                        {{
                           expandedTiers[tier.name]
                              ? "Show less"
                              : "Read more"
                        }}
                        <i
                           class="fas ml-1"
                           :class="
                              expandedTiers[tier.name]
                                 ? 'fa-chevron-up'
                                 : 'fa-chevron-down'
                           "
                        ></i>
                     </button>
                  </div>
               </div>

               <div
                  class="bg-blueGray-800 rounded-2xl p-8 md:p-10 border border-blueGray-700 text-center max-w-3xl mx-auto mt-12"
               >
                  <h3 class="text-2xl font-bold text-white mb-4">
                     Ready to see WaveData Cloud in action?
                  </h3>
                  <p class="text-blueGray-300 mb-6">
                     Book a demo with AMI and we’ll walk through monitoring,
                     alarming, and historical analysis on your fleets.
                  </p>
                  <router-link
                     :to="{ path: '/', hash: '#contact' }"
                     class="inline-block bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
                  >
                     Book a Demo with AMI
                     <i class="fas fa-arrow-right ml-2"></i>
                  </router-link>
               </div>
            </div>
         </section>
      </main>
      <footer-component />
   </div>
</template>

<script>
import NavbarComponent from "@/components/Navbars/IndexNavbar.vue";
import FooterComponent from "@/components/Footers/Footer.vue";
import { setPageSeo, productJsonLd, breadcrumbJsonLd } from "@/utils/seo";
import heroImage from "@/assets/img/ami_wave_pic.jpg";
import fleetviewImage from "../assets/screenshots/fleetview.jpg";
import fleetDrilldownImage from "../assets/screenshots/fleet-drilldown.png";
import historicalImage from "../assets/screenshots/historical.jpg";
import dataexportImage from "../assets/screenshots/dataexport.jpg";
import customScreensImage from "../assets/screenshots/customScreens.png";
import alarmRulesImage from "../assets/screenshots/alarmRules.png";
import pumpMaintenanceImage from "../assets/screenshots/pumpMaintenance.png";

export default {
   name: "WaveData",
   components: {
      NavbarComponent,
      FooterComponent,
   },
   data() {
      return {
         heroImage,
         fleetviewImage,
         customScreensImage,
         alarmRulesImage,
         expandedTiers: {},
         painPoints: [
            {
               title: "Blind spots across the fleet",
               icon: "fas fa-eye-slash",
               challenge:
                  "Multiple pads treating at once, and leadership only hears what someone remembers to call in.",
               solution:
                  "Company-wide Fleets view—online status, treating progress, and drill-down into any asset’s live custom screen.",
               iconWrapClass: "bg-amber-900 text-amber-300 border-amber-400",
            },
            {
               title: "Alarms trapped on the van",
               icon: "fas fa-bell",
               challenge:
                  "Active alarms and events stay local until someone notices—or until the next morning’s review.",
               solution:
                  "Cloud Alarms & Events plus configurable alarm rules so the right people see issues when they fire.",
               iconWrapClass: "bg-orange-900 text-orange-300 border-orange-400",
            },
            {
               title: "No clean post-job picture",
               icon: "fas fa-chart-line",
               challenge:
                  "After the stage, investigating issues or comparing performance means hunting files and tribal notes.",
               solution:
                  "Historical charts, well-stage graphs, and CSV export so you can look back with the full channel history.",
               iconWrapClass: "bg-emerald-900 text-emerald-300 border-emerald-500",
            },
            {
               title: "Clients and experts left guessing",
               icon: "fas fa-user-friends",
               challenge:
                  "Remote experts and granted clients need visibility without a seat in the van or uncontrolled access.",
               solution:
                  "Role-based cloud access—view the jobs, devices, screens, and timeframes you grant; clients stay view only.",
               iconWrapClass: "bg-blue-900 text-lightBlue-300 border-lightBlue-400",
            },
         ],
         monitoringBullets: [
            "Fleets list: connection, job / well / stage, stage progress, and treating time",
            "Expand any fleet to see MPC, pumps, treaters, mixing, and devices",
            "Click an asset to open its assigned custom-screen template",
            "Same live job WaveCommand7 runs in the van—available from anywhere you grant access",
         ],
         monitoringShots: [
            {
               title: "Fleet drill-down into live equipment",
               alt: "WaveData Cloud fleet equipment drill-down",
               image: fleetDrilldownImage,
            },
            {
               title: "Equipment health on a custom screen",
               alt: "WaveData Cloud pump maintenance dashboard",
               image: pumpMaintenanceImage,
            },
         ],
         alarmBullets: [
            "Active Alarms table with filters by time, fleet, and device type",
            "Event log with recent windows or day-range historical lookup",
            "Alarm rules: conditions on channels and states, with configurable actions",
            "Active Alarms widget on custom screens next to treating data",
         ],
         alarmPoints: [
            {
               title: "Live when it matters",
               text: "Surface active alarms in the cloud so supervisors and remote experts are not waiting on a radio call.",
            },
            {
               title: "Rules you own",
               text: "Define which fleets a rule applies to, when it trips, and what happens—then enable or disable without deleting.",
            },
            {
               title: "History for follow-up",
               text: "Pull the event log after the fact to reconstruct what fired and when during the stage.",
            },
         ],
         historyShots: [
            {
               title: "Historical charts",
               alt: "WaveData Cloud historical sensor charts",
               image: historicalImage,
               text: "Pick devices, channels, and a time window to review stored history—separate from live chart widgets.",
            },
            {
               title: "Export for deeper analysis",
               alt: "WaveData Cloud CSV data export",
               image: dataexportImage,
               text: "Download channel data, related events, and job notes as CSV for spreadsheets or your own tools.",
            },
         ],
         historyPoints: [
            {
               title: "Well Stages",
               text: "Filter active and completed stages, open the treatment plan, or jump to PCR / Stage Monitor graphs.",
            },
            {
               title: "Files & reports",
               text: "Keep pad files and generated report PDFs in the cloud repository—download when you need them.",
            },
            {
               title: "Time-range export",
               text: "Recent presets or a day range—export only the equipment that logged in that window.",
            },
            {
               title: "Performance review",
               text: "Use history to investigate issues, review how iron was operated, and check stage performance.",
            },
         ],
         screenBullets: [
            "Meters, charts, gauges, bars, diagrams, and Active Alarms widgets",
            "Templates tied to equipment type for fleet click-through",
            "Browser-based screens for remote viewing from anywhere you grant access",
            "Copy a shared screen into your workspace when you need your own layout",
         ],
         audience: [
            {
               label: "Cloud",
               title: "Area & yard supervisors",
               text: "Watch every fleet’s connection, treating status, and equipment without riding to each location.",
               labelClass: "text-lightBlue-300",
            },
            {
               label: "Cloud",
               title: "Remote experts",
               text: "Help with issues on the jobs and screens you grant—live data without a seat in the van.",
               labelClass: "text-cyan-300",
            },
            {
               label: "Cloud",
               title: "Granted clients",
               text: "Observe progress on the jobs and screens you share—clients remain view only.",
               labelClass: "text-emerald-300",
            },
         ],
         tiers: [
            {
               name: "LITE",
               tagline: "File collection and cloud storage for existing fleets",
               summary:
                  "Straightforward access to job files and sensor logs—including automated conversion from DVCommand data files.",
               accent: "#1d4ed8",
               previewCount: 3,
               features: [
                  {
                     title: "Data Files",
                     text: "Automated file uploaders detect and upload sensor data to the cloud, converting DVCommand data files into CSV for easy integration.",
                  },
                  {
                     title: "Manual User Uploads",
                     text: "Upload documents and files through a straightforward interface.",
                  },
                  {
                     title: "Camera Pictures",
                     text: "Store and access associated camera pictures alongside your sensor data.",
                  },
                  {
                     title: "Cloud Storage",
                     text: "Uploaded data files and images are stored in the cloud, accessible from anywhere you grant access.",
                  },
                  {
                     title: "Easy Download",
                     text: "Download data files and images when you need them.",
                  },
               ],
            },
            {
               name: "PRO",
               tagline: "Historical analysis for growing operations",
               summary:
                  "Database archiving, historical charting, and visualization—so you can look back after the stage without hunting files.",
               accent: "#7e22ce",
               previewCount: 3,
               features: [
                  {
                     title: "All LITE Features",
                     text: "Includes all features from the LITE tier as foundation.",
                  },
                  {
                     title: "Database Storage",
                     text: "Sensor channel data is stored in a scalable database for efficient querying and retrieval.",
                  },
                  {
                     title: "Full Historical Graphing",
                     text: "Visualize sensor trends over time with interactive graphs. Analyze historical patterns and identify key events.",
                  },
                  {
                     title: "Charting Capabilities",
                     text: "Create line, bar, and scatter charts for deeper review of stage and equipment data.",
                  },
                  {
                     title: "Automated Data Ingestion",
                     text: "Optional automated data ingestion, depending on sensor compatibility.",
                  },
               ],
            },
            {
               name: "COMPLETE",
               tagline: "Live fleet monitoring, alarms, and custom screens",
               summary:
                  "Live data streams, cloud alarms and notifications, custom screens, and the full fleet picture—all from the cloud.",
               accent: "#047857",
               previewCount: 3,
               features: [
                  {
                     title: "All PRO Features",
                     text: "Includes all features from the PRO tier as foundation.",
                  },
                  {
                     title: "Full Live Data",
                     text: "Access real-time data streams from connected equipment—the same live job WaveCommand7 runs in the data van.",
                  },
                  {
                     title: "Cloud Alarms & Notifications",
                     text: "Configurable alarm rules with email and SMS so supervisors see issues when they fire—not after the next pad visit.",
                  },
                  {
                     title: "Custom Screens & Dashboards",
                     text: "Design monitoring screens that combine live feeds, historical charts, and key metrics in one view.",
                  },
                  {
                     title: "Real-Time Integration",
                     text: "Integration with compatible real-time sensor equipment for immediate data access.",
                  },
                  {
                     title: "Full Automation",
                     text: "Automated data ingestion for compatible sensors, with comprehensive historical analysis.",
                  },
               ],
            },
         ],
      };
   },
   mounted() {
      setPageSeo({
         title: "WaveData Cloud | Fleet Monitoring & Alarms | AMI",
         description:
            "Remote fleet monitoring, cloud alarms, and historical analysis—so supervisors and experts see the same job WaveCommand7 runs in the data van.",
         keywords:
            "WaveData Cloud, fleet monitoring, cloud alarms, historical analysis, frac data cloud, remote monitoring, custom screens, AMI",
         path: "/wavedata",
         jsonLd: [
            productJsonLd({
               name: "WaveData Cloud",
               description:
                  "Remote fleet monitoring, cloud alarms, and historical analysis for frac operations.",
               path: "/wavedata",
            }),
            breadcrumbJsonLd([
               { name: "Home", path: "/" },
               { name: "WaveData Cloud", path: "/wavedata" },
            ]),
         ],
      });
   },
   methods: {
      visibleTierFeatures(tier) {
         if (this.expandedTiers[tier.name]) {
            return tier.features;
         }
         return tier.features.slice(0, tier.previewCount);
      },
      toggleTier(name) {
         this.expandedTiers = {
            ...this.expandedTiers,
            [name]: !this.expandedTiers[name],
         };
      },
   },
};
</script>
